/**
 * Screen mixin
 *
 * Добавляет общую логику для всех экранов - показ ошибок/спиннера/благодарности
 *
 */

import { GLOBAL_EVENTS, GLOBAL_ACTIONS } from '@/App.vue';

import BaseScreen from '@/components/BaseScreen.vue';
import BaseAlert from '@/components/BaseAlert.vue';

export default {
  components: {
    BaseScreen,
    BaseAlert,
  },
  data() {
    return {
      isPending: false,
      isSpinnerVisible: false,
      isThanksVisible: false,
      isErrorVisible: false,
    };
  },
  staticData: () => ({
    spinnerTimeout: null,
  }),
  computed: {
    screen() {
      return {
        isSpinnerVisible: this.isSpinnerVisible,
        isHeaderHidden: this.isHeaderHidden,
        isFooterHidden: this.isFooterHidden,
        isPatternBackground: this.isPatternBackground,
        pendingText: this.pendingText,
        title: this.title,
      };
    },
    title() {
      if (!this.titleText) return null;
      return {
        text: this.titleText,
      };
    },
    pendingText() {
      return this.$t('main.pending');
    },
    isHeaderHidden() {
      return this.isErrorVisible || this.isThanksVisible;
    },
    isFooterHidden() {
      return !this.$route.meta.footer;
    },
    isPatternBackground() {
      return false;
    },
  },
  mounted() {
    this.addCaseWatcher();
    this.initActions([
      [GLOBAL_EVENTS.INTERACTION_STARTED, this.onInteractionStarted],
      [GLOBAL_EVENTS.INTERACTION_STOPPED, this.onInteractionStopped],
      [GLOBAL_EVENTS.SCREEN_READY, this.onScreenReady],
      [GLOBAL_ACTIONS.RETRY, this.retryErrorAction],
      [GLOBAL_ACTIONS.SUBMIT, this.submitForm],
      [GLOBAL_ACTIONS.GO_HOME, this.goToHomeScreen],
      [GLOBAL_ACTIONS.GO_BACK, this.goBack],
      [GLOBAL_ACTIONS.GO_NEXT, this.goNext],
    ]);
  },
  beforeDestroy() {
    this.clearSpinnerTimeout();
  },
  methods: {
    addCaseWatcher() {
      const unwatch = this.$watch('screenCase', this.onScreenCaseChanged);
      this.$once('hook:beforeDestroy', unwatch);
      this.onScreenCaseChanged(this.screenCase);
    },
    clearSpinnerTimeout() {
      window.clearTimeout(this.spinnerTimeout);
    },
    showPending(delay = 300) {
      this.isPending = true;
      this.clearSpinnerTimeout();
      // Показываем спиннер если нет отсрочки (delay = 0)
      // или операция не завершена за указанный период (по умолчанию 300ms)
      if (delay > 0) {
        this.spinnerTimeout = window.setTimeout(this.showSpinner, delay);
      } else {
        this.showSpinner();
      }
    },
    showSpinner() {
      this.isSpinnerVisible = true;
    },
    hideSpinner() {
      this.isSpinnerVisible = false;
    },
    hidePending() {
      this.isPending = false;
      this.hideSpinner();
      this.clearSpinnerTimeout();
    },
    showError() {
      this.hidePending();
      this.isErrorVisible = true;
    },
    hideError() {
      this.isErrorVisible = false;
    },
    showThanks() {
      this.hidePending();
      this.isThanksVisible = true;
    },
    hideThanks() {
      this.isThanksVisible = false;
    },
    submitForm() {
      return this.$refs.form?.submit();
    },
    setFormFocus() {
      return this.$refs.form?.setFocus(); // Установка фокуса в первое поле формы
    },
    goToHomeScreen() {
      const { home } = this.$router.options.paths;
      if (this.$route.path !== home) {
        this.$router.push(home);
      }
    },
    goToBackScreen() {
      const { back } = this.$route.meta || {};
      if (typeof back === 'number') {
        this.$router.go(back);
      } else if (back) {
        this.$router.push(back);
      }
    },
    goBack() {
      // Функция возврата на предыдущий экран (по голосу или кнопке)
      // Игнорируем вызов, если на экране показана ошибка/успех или выполняется операция
      if (this.isPending || this.isErrorVisible || this.isThanksVisible) return;
      this.goToBackScreen();
    },
    goNext() {
      // Функция перехода на следующий экран (по голосу или кнопке)
    },
    retryErrorAction() {
      // Функция повтора провальной операции
      // Игнорируем вызов, если на экране нет ошибки
      if (!this.isErrorVisible) return;
      this.hideError();
      this.retryAction?.();
    },
    setScreenCase() {
      if (!this.screenCase) return;
      // Когда this.screenCase массив, то можно указать реплику отдельно [case, replic]
      // В остальных ситуациях: реплика = кейс
      const payload = Array.isArray(this.screenCase)
        ? this.screenCase
        : [this.screenCase, this.screenCase];
      this.setCaseAndSayReplic(...payload);
    },
    setCaseAndSayReplic(caseName, replicName) {
      if (caseName) {
        this.$robot.dialogService.setCase(caseName, true, true);
      }
      if (replicName) {
        this.$robot.dialogService.sayReplicByName(replicName, true);
      }
    },
    sayText(text) {
      this.$robot.dialogService.sayText(text, true);
    },
    stopInteraction() {
      this.$robot.interactionService.stop();
    },
    onScreenCaseChanged() {
      // Колбек будет вызван после изменения текущего кейса экрана (this.screenCase)
      this.setScreenCase();
    },
    onScreenReady() {
      // Колбек будет вызван после окончания анимации перехода на экран
      // Подходит для установки фокуса в поле и других манипуляций
      this.setFormFocus();
    },
    onInteractionStarted() {
      // Колбек будет вызван после начала взаимодействия
    },
    onInteractionStopped() {
      this.beforeInteractionStopped?.();
      // Колбек будет вызван после завершения взаимодействия
      // Переход на главную с флагом завершения
      return this.$router.push({
        name: 'Home',
        params: {
          [GLOBAL_EVENTS.INTERACTION_STOPPED]: true,
        },
      });
    },
    initActions(actions) {
      actions.forEach((action) => this.$root.$on(...action));
      this.$once('hook:beforeDestroy', () => {
        actions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
